import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Checkbox, Icon } from "semantic-ui-react";
import { useLoginCheck } from "../../hooks/useLoginCheck";
import "../../styles/scss/dashboard.scss";

function TopPage() {
  useLoginCheck();

  const [lessonNotes, setLessonNotes] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    active_customers_count: 0,
    total_trial_customers_count: 0,
    last_month_trial_customers_count: 0,
  });
  const [pendingAdmissions, setPendingAdmissions] = useState([]);
  const [topInstructors, setTopInstructors] = useState([]);

  const fetchLessonNotes = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_lesson_notes/`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setLessonNotes(data.lesson_notes || []);
    } catch (error) {
      console.error("レッスンメモの取得に失敗しました", error);
      setLessonNotes([]);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_dashboard_stats/`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setDashboardStats(data);
    } catch (error) {
      console.error("統計情報の取得に失敗しました", error);
    }
  };

  // 入会処理待ち (subscription_id が無い顧客)
  // ただし「未経験者専用クラスのみ受講中（正式入会希望なし）」は除外
  const fetchPendingAdmissions = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_customers/`);
      if (!response.ok) throw new Error();
      const data = await response.json();
      const pending = (Array.isArray(data) ? data : [])
        .filter(
          (c) =>
            !c.subscription_id &&
            !(c.has_active_beginner_enrollment && !c.is_full_enrollment_pending)
        )
        .sort((a, b) => {
          // 最新申込順
          const da = a.return_date || a.created_at || "";
          const db = b.return_date || b.created_at || "";
          return db.localeCompare(da);
        })
        .slice(0, 5);
      setPendingAdmissions(pending);
    } catch (e) {
      console.error("入会処理待ちの取得に失敗:", e);
    }
  };

  // インストラクター入会率 TOP5
  const fetchTopInstructors = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const today = new Date();
      const endMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
      const start = new Date(today.getFullYear(), today.getMonth() - 5, 1);
      const startMonth = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}`;
      const response = await fetch(
        `${apiUrl}/api/get_trial_store_analytics/?start_month=${startMonth}&end_month=${endMonth}`,
        { credentials: "include" }
      );
      if (!response.ok) throw new Error();
      const data = await response.json();
      const list = (data.instructors || [])
        .filter((i) => (i.new_trialers || 0) >= 3) // ノイズ排除: 3 名以上の体験
        .slice(0, 5);
      setTopInstructors(list);
    } catch (e) {
      console.error("インストラクターTOP取得に失敗:", e);
    }
  };

  useEffect(() => {
    fetchLessonNotes();
    fetchDashboardStats();
    fetchPendingAdmissions();
    fetchTopInstructors();
  }, []);

  const updateLessonNoteFlag = async (lessonNoteId, flagType, value) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/update_lesson_note_flags/`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lesson_note_id: lessonNoteId,
          [flagType]: value,
        }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      fetchLessonNotes();
    } catch (error) {
      console.error("フラグの更新に失敗しました", error);
    }
  };

  const today = new Date().toLocaleDateString("ja-JP", {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "short",
  });

  const stats = [
    {
      label: "会員数",
      value: dashboardStats.active_customers_count,
      sub: "アクティブ会員",
    },
    {
      label: "前月体験者数",
      value: dashboardStats.last_month_trial_customers_count,
      sub: "先月の体験申込",
    },
    {
      label: "累計体験者数",
      value: dashboardStats.total_trial_customers_count,
      sub: "全期間の累計",
    },
  ];

  const pendingCount = lessonNotes.filter((n) => !n.is_handled).length;

  return (
    <div className="dashboard-page">
      <div className="dash-header">
        <h1>ダッシュボード</h1>
        <span className="dash-date">
          <Icon name="calendar outline" />
          {today}
        </span>
      </div>

      <div className="stat-grid">
        {stats.map((s) => (
          <div className="stat-card" key={s.label}>
            <div className="label">{s.label}</div>
            <div className="value">{(s.value || 0).toLocaleString()}</div>
            <div className="sub">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* 入会処理待ち + インストラクターTOP5 */}
      <div className="dual-grid">
        {/* 入会処理待ち */}
        <div className="panel">
          <div className="panel-header">
            <h2>
              <span className="ja">入会処理待ち</span>
              <span className="ja-sub">Pending Admission</span>
            </h2>
            {pendingAdmissions.length > 0 && (
              <Link
                to="/management/customer-list"
                className="count-pill linkable"
              >
                {pendingAdmissions.length}名 →
              </Link>
            )}
          </div>
          <div className="panel-body">
            {pendingAdmissions.length === 0 ? (
              <div className="empty-state">なし</div>
            ) : (
              <ul className="mini-list">
                {pendingAdmissions.map((c) => (
                  <li key={c.id}>
                    <span className="m-id">#{c.id}</span>
                    <span className="m-name">
                      {`${c.last_name || ""} ${c.first_name || ""}`.trim() ||
                        "—"}
                    </span>
                    <span className="m-studio">{c.studio || "—"}</span>
                    <span className="m-date">
                      {c.return_date || c.created_at || "—"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* インストラクター入会率TOP5 */}
        <div className="panel">
          <div className="panel-header">
            <h2>
              <span className="ja">入会率 TOP5</span>
              <span className="ja-sub">Instructor Ranking · 6mo</span>
            </h2>
            <Link
              to="/management/trial-analytics"
              className="count-pill linkable"
            >
              詳細 →
            </Link>
          </div>
          <div className="panel-body">
            {topInstructors.length === 0 ? (
              <div className="empty-state">データなし</div>
            ) : (
              <ul className="rank-list">
                {topInstructors.map((ins, i) => {
                  const rate = Math.round((ins.conversion_rate || 0) * 100);
                  return (
                    <li key={ins.instructor || i}>
                      <span className={`rk r${i + 1}`}>{i + 1}</span>
                      <span className="rk-name">{ins.instructor || "—"}</span>
                      <span className="rk-meta">
                        <span className="rk-rate">{rate}%</span>
                        <span className="rk-count">
                          {ins.new_joined || 0}/{ins.new_trialers || 0}
                        </span>
                      </span>
                      <div className="rk-bar">
                        <span
                          className="fill"
                          style={{ width: `${Math.min(rate, 100)}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <h2>レッスン連絡事項</h2>
          {lessonNotes.length > 0 && (
            <span className="count-pill">未対応 {pendingCount}件</span>
          )}
        </div>
        <div className="panel-body">
          {lessonNotes && lessonNotes.length > 0 ? (
            <div className="grid-table lesson-notes-grid">
              <div className="grid-row grid-head">
                <div className="cell">レッスン日</div>
                <div className="cell">スタジオ</div>
                <div className="cell">レッスン名</div>
                <div className="cell">備考</div>
                <div className="cell">イントラ</div>
                <div className="cell center">要確認</div>
                <div className="cell center">対応済み</div>
              </div>
              {lessonNotes.map((lessonNote) => (
                <div
                  key={lessonNote.id}
                  className={`grid-row ${
                    lessonNote.is_handled ? "is-handled" : ""
                  }`}
                >
                  <div className="cell">{lessonNote.date}</div>
                  <div className="cell">{lessonNote.studio_name}</div>
                  <div className="cell">{lessonNote.lesson_name}</div>
                  <div className="cell">{lessonNote.message}</div>
                  <div className="cell">{lessonNote.instructor}</div>
                  <div className="cell center">
                    <Checkbox
                      checked={lessonNote.needs_confirmation}
                      onChange={(_, { checked }) =>
                        updateLessonNoteFlag(
                          lessonNote.id,
                          "needs_confirmation",
                          checked
                        )
                      }
                    />
                  </div>
                  <div className="cell center">
                    <Checkbox
                      checked={lessonNote.is_handled}
                      onChange={(_, { checked }) =>
                        updateLessonNoteFlag(
                          lessonNote.id,
                          "is_handled",
                          checked
                        )
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">メモがありません</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TopPage;
